import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      uploadFailedGeneric: "Upload failed. Please try again.", monthlyCostDollar: "Monthly cost ($)", processingFiles: "Analyzing your files…", server: "Server", cpuPercent: "CPU %", memoryPercent: "Memory %", details: "Details", cpuMemoryPerformance: "CPU & memory performance", costComparison: "Cost comparison", monthlyCostComparison: "Monthly cost comparison", cpuCores: "CPU cores", totalMemory: "Total memory", memoryPerCore: "Memory per core", recommendations: "Recommendations", performanceMetricsOverview: "Performance metrics overview", instanceRecommendations: "Instance recommendations", vcpus: "vCPUs",
      nav: { workspace: "Workspace", newAnalysis: "New Analysis", history: "History", shared: "Shared with me", admin: "Admin", customerUploads: "Customer uploads", help: "Help", guide: "SSAT Guide", collector: "Get Collector", logout: "Logout" },
      common: { cancel: "Cancel", close: "Close" },
      analyzer: { heading: "Right-size your SQL Server workload", subheading: "Upload the SSAT collector output to get an Amazon RDS recommendation based on the real workload.", upload: "Upload SSAT files", customer: "Customer / workload name", export: "Export", tips: "Tips", share: "Share this analysis", viewing: "Viewing", startNew: "Start a new analysis" },
      history: { subtitle: "Your saved analyses. Analyses are kept for 180 days.", emptyTitle: "No saved analyses yet", search: "Search customer or workload…", customer: "Customer / workload", servers: "Servers", uploaded: "Uploaded", actions: "Actions", open: "Open", share: "Share", delete: "Delete", email: "Email address" },
      shared: { subtitle: "Analyses that other people shared with you.", emptyTitle: "Nothing shared with you yet", from: "Shared by", on: "Shared on" },
      admin: { subtitle: "Every analysis uploaded by any user. Click a row to open it. Administrators only.", refresh: "Refresh", total: "Total uploads", users: "Users", servers: "Servers analysed", last: "Latest upload", user: "User", sharedWith: "Shared with", deleted: "deleted" },
      title: "RDSTools - SQL Server Assessment",
      subtitle: "Intelligent AWS RDS recommendations with enhanced pricing analysis",
      ssatGuide: "SSAT Guide",
      language: "Language",
      ssatCollection: "SSAT Data Collection",
      downloadDescription: "Download the SSAT Collector (a ZIP with a small GUI and PowerShell scripts) to gather performance data from your SQL Server instances. It collects the CPU, memory and I/O metrics required for analysis.",
      runDescription: "Run this script on your SQL Server to generate the ZIP files needed for analysis.",
      downloadButton: "Download SSATcollector.ps1",
      fileUpload: "File Upload",
      uploadDescription: "Upload your SSAT ZIP files for analysis. You can upload multiple files for batch processing.",
      dragDrop: "Drag & drop SSAT ZIP files here",
      dragActive: "Drop files here...",
      clickToSelect: "or click to select files (supports multiple files)",
      selectedFiles: "Selected Files",
      removeFile: "Remove file",
      analyzeButton: "Analyze Files",
      analysisResults: "Analysis Results",
      processedFiles: "Processed Files",
      successfulFiles: "Successful Files",
      failedFiles: "Failed Files",
      fileName: "File Name",
      serverName: "Server Name",
      recommendation: "Recommendation",
      monthlyPrice: "Monthly Price",
      actions: "Actions",
      viewDetails: "View Details",
      exportResults: "Export Results",
      performanceMetrics: "Performance Metrics",
      cpuUsage: "CPU Usage",
      memoryUsage: "Memory Usage",
      iopsUsage: "IOPS Usage",
      throughput: "Throughput",
      average: "Average",
      maximum: "Maximum",
      p95: "95th Percentile",
      recommendedInstance: "Recommended Instance",
      specifications: "Specifications",
      cpu: "CPU",
      memory: "Memory",
      iops: "IOPS",
      pricing: "Pricing",
      hourlyPrice: "Hourly Price",
      explanation: "Explanation",
      score: "Score",
      ebsType: "EBS Type",
      basicUsage: "Basic Usage",
      commandOptions: "Command Line Options",
      advancedExamples: "Advanced Examples",
      serverlistParam: "SQL Server instance name or IP address",
      authParam: "Authentication: 'w' for Windows, 's' for SQL",
      loginParam: "SQL login username (required for SQL auth)",
      passwordParam: "SQL login password (required for SQL auth)",
      collectiontimeParam: "Collection duration in minutes (optional)",
      outputpathParam: "Output directory (default: current folder)",
      cleanupParam: "Clean up temporary files (switch)",
      terminateParam: "Terminate existing collection (switch)",
      exportParam: "Export collected data (switch)",
      compressParam: "Compress output files to ZIP (switch)",
      databaseParam: "Target database (default: msdb). Required for RDS — use admin or user DB.",
      customernameParam: "Customer name prefix for ZIP filename (optional)",
      tipCompress: "Use -compress switch to automatically create ZIP files ready for upload to this web tool.",
      requirementsPowershell: "PowerShell 5.1+, SQL Server access, and appropriate authentication credentials.",
      usageInstructions: "Run the PowerShell script on your SQL Server with administrator privileges. The script will generate a ZIP file containing performance metrics that you can upload above for analysis.",
      analyzing: "Analyzing files...",
      uploadSuccess: "Files uploaded successfully",
      uploadError: "Error uploading files",
      noFiles: "No files selected",
      monthlyComparison: "Monthly Cost Comparison",
      monthlyCost: "Monthly Cost ($)",
      performanceOverview: "Performance Overview",
      contact: "Contact",
      feedback: "Feedback",
      feedbackForm: "Submit Feedback Form",
      linkedinProfile: "LinkedIn Profile",
      footerNote: "SSATWeb - SQL Server Assessment Tool for AWS RDS Migration Planning",
      detailedGuide: {
        title: "SSAT Collector - Detailed Guide",
        heading: "SSAT Collector - Comprehensive Guide",
        description: "Complete guide for SQL Server assessment, right-sizing, and AWS RDS migration planning using SSAT.",
        downloadSection: "Download and Setup",
        step1: "Step 1: Download",
        step1Description: "Download SSATcollector.zip from the main application page.",
        step2: "Step 2: Extract",
        step2Description: "Extract to a folder on your SQL Server or a machine with network access. Contains:",
        launcherFile: "SSATcollector_launcher.ps1 — Main entry point (auto-detects PowerShell version)",
        modernFile: "SSATcollector.ps1 — Modern PowerShell (5.1+ with SqlServer module)",
        compatibleFile: "SSATcollector_compatible.ps1 — Compatible version for older environments",
        basicUsageSection: "Basic Usage",
        alwaysUseLauncher: "Always use the launcher! It auto-detects your PowerShell version.",
        windowsAuth: "Windows Authentication",
        sqlAuth: "SQL Server Authentication",
        parametersSection: "Complete Parameter Reference",
        operationsSection: "Detailed Operations Guide",
        dataCollection: "Data Collection",
        dataCollectionDesc: "Collects CPU, memory, IOPS, throughput, SQL Server edition, version, and hyperthreading status. Runs continuously for the specified duration.",
        exportOptions: "Export Options",
        exportOptionsDesc: "After collection, export for analysis:",
        exportOnly: "Export Only (-export): Individual CSV files per metric",
        exportCompress: "Export + Compress (-export -compress): CSV files packaged into a ZIP for upload",
        cleanupOperations: "Cleanup Operations",
        cleanupDesc: "Remove temporary collection files after export.",
        terminateCollection: "Terminate Collection",
        terminateDesc: "Stop a running collection before completion.",
        workflowsSection: "Common Workflows",
        quickAssessment: "Quick Assessment (1 hour)",
        comprehensiveAssessment: "Comprehensive Assessment (24 hours)",
        multiServerAssessment: "Multiple Server Assessment",
        sqlAuthEncrypted: "SQL Authentication",
        troubleshootingSection: "Troubleshooting",
        commonIssues: "Common Issues",
        executionPolicy: "PowerShell Execution Policy: Run Set-ExecutionPolicy Bypass -Scope Process",
        sqlServerModule: "SqlServer Module Missing: Launcher uses compatible version automatically.",
        connectionIssues: "Connection Issues: Ensure appropriate permissions (VIEW SERVER STATE).",
        outputFiles: "Output Files",
        outputFilesDesc: "After export, the ZIP contains:",
        compressedArchive: "ServerName_SSAT_timestamp.zip — Upload this to the web tool",
        cpuMetrics: "CPU.csv — SQL Server CPU utilization over time",
        memoryMetrics: "MEM.csv — Memory usage, PLE, available memory",
        ioMetrics: "IO.csv — IOPS and throughput (TotalIOPs, Throuput)",
        databaseMetrics: "CPUINFO.csv — Cores, HT ratio, VM type, SQL Edition, SQL Version",
        engineSection: "How the Engine Works",
        engineDesc: "The recommendation engine right-sizes based on actual workload:",
        engineStep1: "1. CPU Sizing: CPU count / 4 = size class (xlarge, 2xlarge, etc.)",
        engineStep2: "2. Hyperthreading is not used for sizing (the size comes from the CPU count)",
        engineStep3: "3. Memory Family: Ratio ≤4:1 → m-family, >4:1 → r-family, very high → x2iedn",
        engineStep4: "4. Utilization Scaling: High CPU+MEM → scale up, Low both → scale down",
        engineStep5: "5. IOPS/Throughput: Find smallest instance meeting all 4 constraints",
        engineStep6: "6. Memory Validation: Ensure instance memory ≥ source server memory",
        editionSection: "Edition Handling",
        editionExpress: "Express → Fixed db.t3.xlarge (4 vCPU, 16 GB)",
        editionDeveloper: "Developer → Fixed db.r6i.xlarge (4 vCPU, 32 GB)",
        editionWeb: "Web → Max 4xlarge (16 vCPU)",
        editionStandard: "Standard → Max 12xlarge (48 vCPU)",
        editionEnterprise: "Enterprise → No limit",
        featuresSection: "Web App Features",
        featureHistory: "Upload History — View and reload past assessments",
        featureShare: "Share — Share analysis with colleagues by email",
        featureAdmin: "Admin Dashboard — View all uploads across all users",
        featureExport: "Export — CSV, JSON, and PDF report downloads",
        featureChatbot: "AI Assistant — Ask questions about your analysis (Amazon only)",
        featurePricing: "Live Pricing — Real-time AWS pricing via Pricing API",
        backToMain: "Back to Main Application"
      }
    }
  },
  es: {
    translation: {
      uploadFailedGeneric: "Error al subir. Inténtelo de nuevo.", monthlyCostDollar: "Costo mensual ($)", processingFiles: "Analizando sus archivos…", server: "Servidor", cpuPercent: "CPU %", memoryPercent: "Memoria %", details: "Detalles", cpuMemoryPerformance: "Rendimiento de CPU y memoria", costComparison: "Comparación de costos", monthlyCostComparison: "Comparación de costo mensual", cpuCores: "Núcleos de CPU", totalMemory: "Memoria total", memoryPerCore: "Memoria por núcleo", recommendations: "Recomendaciones", performanceMetricsOverview: "Resumen de métricas de rendimiento", instanceRecommendations: "Recomendaciones de instancia", vcpus: "vCPU",
      nav: { workspace: "Espacio de trabajo", newAnalysis: "Nuevo análisis", history: "Historial", shared: "Compartidos conmigo", admin: "Administración", customerUploads: "Cargas de clientes", help: "Ayuda", guide: "Guía SSAT", collector: "Descargar colector", logout: "Salir" },
      common: { cancel: "Cancelar", close: "Cerrar" },
      analyzer: { heading: "Dimensione correctamente su carga SQL Server", subheading: "Suba la salida del colector SSAT para obtener una recomendación de Amazon RDS basada en la carga real.", upload: "Subir archivos SSAT", customer: "Cliente / carga de trabajo", export: "Exportar", tips: "Consejos", share: "Compartir este análisis", viewing: "Viendo", startNew: "Iniciar un nuevo análisis" },
      history: { subtitle: "Sus análisis guardados. Se conservan 180 días.", emptyTitle: "Aún no hay análisis guardados", search: "Buscar cliente o carga…", customer: "Cliente / carga", servers: "Servidores", uploaded: "Subido", actions: "Acciones", open: "Abrir", share: "Compartir", delete: "Eliminar", email: "Correo electrónico" },
      shared: { subtitle: "Análisis que otras personas compartieron con usted.", emptyTitle: "Aún no le han compartido nada", from: "Compartido por", on: "Compartido el" },
      admin: { subtitle: "Todos los análisis subidos por cualquier usuario. Haga clic en una fila para abrirla. Solo administradores.", refresh: "Actualizar", total: "Cargas totales", users: "Usuarios", servers: "Servidores analizados", last: "Última carga", user: "Usuario", sharedWith: "Compartido con", deleted: "eliminado" },
      title: "RDSTools - Evaluación de SQL Server",
      subtitle: "Recomendaciones inteligentes de AWS RDS con análisis de precios mejorado",
      ssatGuide: "Guía SSAT",
      language: "Idioma",
      ssatCollection: "Recopilación de Datos SSAT",
      downloadDescription: "Descarga el SSAT Collector (un ZIP con una interfaz sencilla y scripts de PowerShell) para recopilar datos de rendimiento de tus instancias de SQL Server. Recopila las métricas de CPU, memoria e I/O necesarias para el análisis.",
      runDescription: "Ejecuta este script en tu SQL Server para generar los archivos ZIP necesarios para el análisis.",
      downloadButton: "Descargar SSATcollector.ps1",
      fileUpload: "Subida de Archivos",
      uploadDescription: "Sube tus archivos ZIP de SSAT para análisis. Puedes subir múltiples archivos para procesamiento por lotes.",
      dragDrop: "Arrastra y suelta archivos ZIP de SSAT aquí",
      dragActive: "Suelta los archivos aquí...",
      clickToSelect: "o haz clic para seleccionar archivos (soporta múltiples archivos)",
      selectedFiles: "Archivos Seleccionados",
      removeFile: "Eliminar archivo",
      analyzeButton: "Analizar Archivos",
      analysisResults: "Resultados del Análisis",
      processedFiles: "Archivos Procesados",
      successfulFiles: "Archivos Exitosos",
      failedFiles: "Archivos Fallidos",
      fileName: "Nombre del Archivo",
      serverName: "Nombre del Servidor",
      recommendation: "Recomendación",
      monthlyPrice: "Precio Mensual",
      actions: "Acciones",
      viewDetails: "Ver Detalles",
      exportResults: "Exportar Resultados",
      performanceMetrics: "Métricas de Rendimiento",
      cpuUsage: "Uso de CPU",
      memoryUsage: "Uso de Memoria",
      iopsUsage: "Uso de IOPS",
      throughput: "Rendimiento",
      average: "Promedio",
      maximum: "Máximo",
      p95: "Percentil 95",
      recommendedInstance: "Instancia Recomendada",
      specifications: "Especificaciones",
      cpu: "CPU",
      memory: "Memoria",
      iops: "IOPS",
      pricing: "Precios",
      hourlyPrice: "Precio por Hora",
      explanation: "Explicación",
      score: "Puntuación",
      ebsType: "Tipo EBS",
      basicUsage: "Uso Básico",
      commandOptions: "Opciones de Línea de Comandos",
      advancedExamples: "Ejemplos Avanzados",
      serverlistParam: "Nombre de instancia de SQL Server o dirección IP",
      authParam: "Autenticación: 'w' para Windows, 's' para SQL",
      loginParam: "Nombre de usuario SQL (requerido para auth SQL)",
      passwordParam: "Contraseña SQL (requerida para auth SQL)",
      collectiontimeParam: "Duración de recopilación en minutos (opcional)",
      outputpathParam: "Directorio de salida (predeterminado: carpeta actual)",
      cleanupParam: "Limpiar archivos temporales (interruptor)",
      terminateParam: "Terminar recopilación existente (interruptor)",
      exportParam: "Exportar datos recopilados (interruptor)",
      compressParam: "Comprimir archivos de salida a ZIP (interruptor)",
      databaseParam: "Base de datos destino (default: msdb). Requerido para RDS.",
      customernameParam: "Prefijo de nombre de cliente para archivo ZIP (opcional)",
      tipCompress: "Usa el interruptor -compress para crear automáticamente archivos ZIP listos para subir a esta herramienta web.",
      requirementsPowershell: "PowerShell 5.1+, acceso a SQL Server y credenciales de autenticación apropiadas.",
      usageInstructions: "Ejecuta el script de PowerShell en tu SQL Server con privilegios de administrador. El script generará un archivo ZIP que contiene métricas de rendimiento que puedes subir arriba para análisis.",
      analyzing: "Analizando archivos...",
      uploadSuccess: "Archivos subidos exitosamente",
      uploadError: "Error al subir archivos",
      noFiles: "No hay archivos seleccionados",
      monthlyComparison: "Comparación de Costos Mensuales",
      monthlyCost: "Costo Mensual ($)",
      performanceOverview: "Resumen de Rendimiento",
      contact: "Contacto",
      feedback: "Comentarios",
      feedbackForm: "Enviar Formulario de Comentarios",
      linkedinProfile: "Perfil de LinkedIn",
      footerNote: "SSATWeb - Herramienta de Evaluación para Migración de SQL Server a AWS RDS",
      detailedGuide: {
        title: "SSAT Collector - Guía Detallada",
        heading: "SSAT Collector - Guía Completa",
        description: "Guía completa para evaluación de SQL Server, dimensionamiento y planificación de migración a AWS RDS.",
        downloadSection: "Descarga y Configuración",
        step1: "Paso 1: Descargar",
        step1Description: "Descargue SSATcollector.zip desde la página principal.",
        step2: "Paso 2: Extraer",
        step2Description: "Extraiga en una carpeta en su SQL Server. Contiene:",
        launcherFile: "SSATcollector_launcher.ps1 — Punto de entrada principal",
        modernFile: "SSATcollector.ps1 — PowerShell moderno (5.1+)",
        compatibleFile: "SSATcollector_compatible.ps1 — Versión compatible",
        basicUsageSection: "Uso Básico",
        alwaysUseLauncher: "¡Siempre use el launcher! Detecta su versión de PowerShell automáticamente.",
        windowsAuth: "Autenticación Windows",
        sqlAuth: "Autenticación SQL Server",
        parametersSection: "Referencia de Parámetros",
        operationsSection: "Guía de Operaciones",
        dataCollection: "Recolección de Datos",
        dataCollectionDesc: "Recolecta CPU, memoria, IOPS, throughput, edición SQL Server, versión y estado de hyperthreading.",
        exportOptions: "Opciones de Exportación",
        exportOptionsDesc: "Después de la recolección, exporte para análisis:",
        exportOnly: "Solo Exportar (-export): Archivos CSV individuales",
        exportCompress: "Exportar + Comprimir (-export -compress): ZIP para subir",
        cleanupOperations: "Operaciones de Limpieza",
        cleanupDesc: "Eliminar archivos temporales después de exportar.",
        terminateCollection: "Terminar Recolección",
        terminateDesc: "Detener una recolección en progreso.",
        workflowsSection: "Flujos de Trabajo Comunes",
        quickAssessment: "Evaluación Rápida (1 hora)",
        comprehensiveAssessment: "Evaluación Completa (24 horas)",
        multiServerAssessment: "Evaluación Multi-Servidor",
        sqlAuthEncrypted: "Autenticación SQL",
        troubleshootingSection: "Resolución de Problemas",
        commonIssues: "Problemas Comunes",
        executionPolicy: "Política de Ejecución: Ejecute Set-ExecutionPolicy Bypass -Scope Process",
        sqlServerModule: "Módulo SqlServer faltante: El launcher usa versión compatible automáticamente.",
        connectionIssues: "Problemas de conexión: Asegure permisos apropiados (VIEW SERVER STATE).",
        outputFiles: "Archivos de Salida",
        outputFilesDesc: "Después de exportar, el ZIP contiene:",
        compressedArchive: "ServerName_SSAT_timestamp.zip — Suba esto a la herramienta web",
        cpuMetrics: "CPU.csv — Utilización de CPU en el tiempo",
        memoryMetrics: "MEM.csv — Uso de memoria, PLE",
        ioMetrics: "IO.csv — IOPS y throughput",
        databaseMetrics: "CPUINFO.csv — Cores, HT, tipo VM, Edición SQL, Versión SQL",
        engineSection: "Cómo Funciona el Motor",
        engineDesc: "El motor dimensiona basándose en la carga de trabajo real:",
        engineStep1: "1. Dimensionamiento CPU: cantidad de CPU / 4 = clase de tamaño (xlarge, 2xlarge, etc.)",
        engineStep2: "2. Hyperthreading no se usa para dimensionar (el tamaño sale de la cantidad de CPU)",
        engineStep3: "3. Familia por Memoria: Ratio ≤4:1 → familia-m, >4:1 → familia-r, muy alto → x2iedn",
        engineStep4: "4. Escalamiento por Utilización: CPU+MEM altos → escalar arriba, ambos bajos → escalar abajo",
        engineStep5: "5. IOPS/Throughput: Encontrar instancia más pequeña cumpliendo las 4 restricciones",
        engineStep6: "6. Validación Memoria: Asegurar memoria instancia ≥ memoria servidor origen",
        editionSection: "Manejo de Ediciones",
        editionExpress: "Express → Fijo db.t3.xlarge (4 vCPU, 16 GB)",
        editionDeveloper: "Developer → Fijo db.r6i.xlarge (4 vCPU, 32 GB)",
        editionWeb: "Web → Máximo 4xlarge (16 vCPU)",
        editionStandard: "Standard → Máximo 12xlarge (48 vCPU)",
        editionEnterprise: "Enterprise → Sin límite",
        featuresSection: "Funciones de la App Web",
        featureHistory: "Historial — Ver y recargar evaluaciones anteriores",
        featureShare: "Compartir — Compartir análisis con colegas por email",
        featureAdmin: "Panel Admin — Ver todas las cargas de todos los usuarios",
        featureExport: "Exportar — CSV, JSON y reportes PDF",
        featureChatbot: "Asistente IA — Pregunte sobre su análisis",
        featurePricing: "Precios en Vivo — Precios AWS en tiempo real",
        backToMain: "Volver a la Aplicación"
      }
    }
  },
  ja: {
    translation: {
      title: "RDSTools - SQL Server評価",
      subtitle: "拡張価格分析によるインテリジェントなAWS RDS推奨事項",
      ssatGuide: "SSATガイド",
      language: "言語",
      ssatCollection: "SSATデータ収集",
      downloadDescription: "SQL Serverインスタンスからパフォーマンスデータを収集するSSAT Collector PowerShellスクリプトをダウンロードします。このスクリプトは、分析に必要なCPU、メモリ、I/Oメトリクスを収集します。",
      runDescription: "このスクリプトをSQL Serverで実行して、分析に必要なZIPファイルを生成します。",
      downloadButton: "SSATcollector.ps1をダウンロード",
      fileUpload: "ファイルアップロード",
      uploadDescription: "分析用のSSAT ZIPファイルをアップロードします。バッチ処理用に複数のファイルをアップロードできます。",
      dragDrop: "SSAT ZIPファイルをここにドラッグ＆ドロップ",
      dragActive: "ファイルをここにドロップ...",
      clickToSelect: "またはクリックしてファイルを選択（複数ファイル対応）",
      selectedFiles: "選択されたファイル",
      removeFile: "ファイルを削除",
      analyzeButton: "ファイルを分析",
      analysisResults: "分析結果",
      processedFiles: "処理されたファイル",
      successfulFiles: "成功したファイル",
      failedFiles: "失敗したファイル",
      fileName: "ファイル名",
      serverName: "サーバー名",
      recommendation: "推奨事項",
      monthlyPrice: "月額料金",
      actions: "アクション",
      viewDetails: "詳細を表示",
      exportResults: "結果をエクスポート",
      performanceMetrics: "パフォーマンスメトリクス",
      cpuUsage: "CPU使用率",
      memoryUsage: "メモリ使用率",
      iopsUsage: "IOPS使用率",
      throughput: "スループット",
      average: "平均",
      maximum: "最大",
      p95: "95パーセンタイル",
      recommendedInstance: "推奨インスタンス",
      specifications: "仕様",
      cpu: "CPU",
      memory: "メモリ",
      iops: "IOPS",
      pricing: "価格",
      hourlyPrice: "時間料金",
      explanation: "説明",
      score: "スコア",
      ebsType: "EBSタイプ",
      basicUsage: "基本的な使用方法",
      commandOptions: "コマンドラインオプション",
      advancedExamples: "高度な例",
      serverlistParam: "SQL Serverインスタンス名またはIPアドレス",
      authParam: "認証：Windowsの場合は'w'、SQLの場合は's'",
      loginParam: "SQLログインユーザー名（SQL認証に必要）",
      passwordParam: "SQLログインパスワード（SQL認証に必要）",
      collectiontimeParam: "収集時間（分）（オプション）",
      outputpathParam: "出力ディレクトリ（デフォルト：現在のフォルダ）",
      cleanupParam: "一時ファイルをクリーンアップ（スイッチ）",
      terminateParam: "既存の収集を終了（スイッチ）",
      exportParam: "収集されたデータをエクスポート（スイッチ）",
      compressParam: "出力ファイルをZIPに圧縮（スイッチ）",
      databaseParam: "対象データベース（デフォルト: msdb）。RDSの場合必須。",
      customernameParam: "ZIPファイル名の顧客名プレフィックス（オプション）",
      tipCompress: "-compressスイッチを使用して、このWebツールにアップロード可能なZIPファイルを自動作成します。",
      requirementsPowershell: "PowerShell 5.1+、SQL Serverアクセス、適切な認証資格情報。",
      usageInstructions: "管理者権限でSQL ServerでPowerShellスクリプトを実行します。スクリプトは、上記で分析用にアップロードできるパフォーマンスメトリクスを含むZIPファイルを生成します。",
      analyzing: "ファイルを分析中...",
      uploadSuccess: "ファイルが正常にアップロードされました",
      uploadError: "ファイルのアップロードエラー",
      noFiles: "ファイルが選択されていません",
      monthlyComparison: "月額コスト比較",
      monthlyCost: "月額コスト（$）",
      performanceOverview: "パフォーマンス概要",
      contact: "連絡先",
      feedback: "フィードバック",
      feedbackForm: "フィードバックフォームを送信",
      linkedinProfile: "LinkedInプロフィール",
      footerNote: "SSATWeb - AWS RDS移行計画のためのSQL Server評価ツール"
    }
  },
  ko: {
    translation: {
      title: "RDSTools - SQL Server 평가",
      subtitle: "향상된 가격 분석을 통한 지능형 AWS RDS 권장사항",
      ssatGuide: "SSAT 가이드",
      language: "언어",
      ssatCollection: "SSAT 데이터 수집",
      downloadDescription: "SQL Server 인스턴스에서 성능 데이터를 수집하기 위한 SSAT Collector PowerShell 스크립트를 다운로드합니다. 이 스크립트는 분석에 필요한 CPU, 메모리, I/O 메트릭을 수집합니다.",
      runDescription: "SQL Server에서 이 스크립트를 실행하여 분석에 필요한 ZIP 파일을 생성합니다.",
      downloadButton: "SSATcollector.ps1 다운로드",
      fileUpload: "파일 업로드",
      uploadDescription: "분석을 위해 SSAT ZIP 파일을 업로드합니다. 배치 처리를 위해 여러 파일을 업로드할 수 있습니다.",
      dragDrop: "SSAT ZIP 파일을 여기에 드래그 앤 드롭",
      dragActive: "파일을 여기에 드롭하세요...",
      clickToSelect: "또는 클릭하여 파일 선택 (다중 파일 지원)",
      selectedFiles: "선택된 파일",
      removeFile: "파일 제거",
      analyzeButton: "파일 분석",
      analysisResults: "분석 결과",
      processedFiles: "처리된 파일",
      successfulFiles: "성공한 파일",
      failedFiles: "실패한 파일",
      fileName: "파일명",
      serverName: "서버명",
      recommendation: "권장사항",
      monthlyPrice: "월 요금",
      actions: "작업",
      viewDetails: "세부사항 보기",
      exportResults: "결과 내보내기",
      performanceMetrics: "성능 메트릭",
      cpuUsage: "CPU 사용률",
      memoryUsage: "메모리 사용률",
      iopsUsage: "IOPS 사용률",
      throughput: "처리량",
      average: "평균",
      maximum: "최대",
      p95: "95 백분위수",
      recommendedInstance: "권장 인스턴스",
      specifications: "사양",
      cpu: "CPU",
      memory: "메모리",
      iops: "IOPS",
      pricing: "가격",
      hourlyPrice: "시간당 요금",
      explanation: "설명",
      score: "점수",
      ebsType: "EBS 유형",
      basicUsage: "기본 사용법",
      commandOptions: "명령줄 옵션",
      advancedExamples: "고급 예제",
      serverlistParam: "SQL Server 인스턴스 이름 또는 IP 주소",
      authParam: "인증: Windows의 경우 'w', SQL의 경우 's'",
      loginParam: "SQL 로그인 사용자명 (SQL 인증 필요)",
      passwordParam: "SQL 로그인 비밀번호 (SQL 인증 필요)",
      collectiontimeParam: "수집 시간(분) (선택사항)",
      outputpathParam: "출력 디렉토리 (기본값: 현재 폴더)",
      cleanupParam: "임시 파일 정리 (스위치)",
      terminateParam: "기존 수집 종료 (스위치)",
      exportParam: "수집된 데이터 내보내기 (스위치)",
      compressParam: "출력 파일을 ZIP으로 압축 (스위치)",
      databaseParam: "대상 데이터베이스 (기본: msdb). RDS의 경우 필수.",
      customernameParam: "ZIP 파일명의 고객 이름 접두사 (선택사항)",
      tipCompress: "-compress 스위치를 사용하여 이 웹 도구에 업로드할 수 있는 ZIP 파일을 자동으로 생성합니다.",
      requirementsPowershell: "PowerShell 5.1+, SQL Server 액세스, 적절한 인증 자격 증명.",
      usageInstructions: "관리자 권한으로 SQL Server에서 PowerShell 스크립트를 실행합니다. 스크립트는 위에서 분석을 위해 업로드할 수 있는 성능 메트릭이 포함된 ZIP 파일을 생성합니다.",
      analyzing: "파일 분석 중...",
      uploadSuccess: "파일이 성공적으로 업로드되었습니다",
      uploadError: "파일 업로드 오류",
      noFiles: "선택된 파일이 없습니다",
      monthlyComparison: "월간 비용 비교",
      monthlyCost: "월간 비용 ($)",
      performanceOverview: "성능 개요",
      contact: "연락처",
      feedback: "피드백",
      feedbackForm: "피드백 양식 제출",
      linkedinProfile: "LinkedIn 프로필",
      footerNote: "SSATWeb - AWS RDS 마이그레이션 계획을 위한 SQL Server 평가 도구"
    }
  },
  pt: {
    translation: {
      title: "RDSTools - Avaliação do SQL Server",
      subtitle: "Recomendações inteligentes do AWS RDS com análise de preços aprimorada",
      ssatGuide: "Guia SSAT",
      language: "Idioma",
      ssatCollection: "Coleta de Dados SSAT",
      downloadDescription: "Baixe o script PowerShell SSAT Collector para coletar dados de desempenho de suas instâncias do SQL Server. Este script coleta métricas de CPU, Memória e I/O necessárias para análise.",
      runDescription: "Execute este script no seu SQL Server para gerar os arquivos ZIP necessários para análise.",
      downloadButton: "Baixar SSATcollector.ps1",
      fileUpload: "Upload de Arquivos",
      uploadDescription: "Faça upload dos seus arquivos ZIP SSAT para análise. Você pode fazer upload de múltiplos arquivos para processamento em lote.",
      dragDrop: "Arraste e solte arquivos ZIP SSAT aqui",
      dragActive: "Solte os arquivos aqui...",
      clickToSelect: "ou clique para selecionar arquivos (suporta múltiplos arquivos)",
      selectedFiles: "Arquivos Selecionados",
      removeFile: "Remover arquivo",
      analyzeButton: "Analisar Arquivos",
      analysisResults: "Resultados da Análise",
      processedFiles: "Arquivos Processados",
      successfulFiles: "Arquivos Bem-sucedidos",
      failedFiles: "Arquivos Falhados",
      fileName: "Nome do Arquivo",
      serverName: "Nome do Servidor",
      recommendation: "Recomendação",
      monthlyPrice: "Preço Mensal",
      actions: "Ações",
      viewDetails: "Ver Detalhes",
      exportResults: "Exportar Resultados",
      performanceMetrics: "Métricas de Desempenho",
      cpuUsage: "Uso de CPU",
      memoryUsage: "Uso de Memória",
      iopsUsage: "Uso de IOPS",
      throughput: "Taxa de Transferência",
      average: "Média",
      maximum: "Máximo",
      p95: "95º Percentil",
      recommendedInstance: "Instância Recomendada",
      specifications: "Especificações",
      cpu: "CPU",
      memory: "Memória",
      iops: "IOPS",
      pricing: "Preços",
      hourlyPrice: "Preço por Hora",
      explanation: "Explicação",
      score: "Pontuação",
      ebsType: "Tipo EBS",
      basicUsage: "Uso Básico",
      commandOptions: "Opções de Linha de Comando",
      advancedExamples: "Exemplos Avançados",
      serverlistParam: "Nome da instância do SQL Server ou endereço IP",
      authParam: "Autenticação: 'w' para Windows, 's' para SQL",
      loginParam: "Nome de usuário de login SQL (necessário para auth SQL)",
      passwordParam: "Senha de login SQL (necessária para auth SQL)",
      collectiontimeParam: "Duração da coleta em minutos (opcional)",
      outputpathParam: "Diretório de saída (padrão: pasta atual)",
      cleanupParam: "Limpar arquivos temporários (switch)",
      terminateParam: "Terminar coleta existente (switch)",
      exportParam: "Exportar dados coletados (switch)",
      compressParam: "Comprimir arquivos de saída para ZIP (switch)",
      databaseParam: "Banco de dados alvo (padrão: msdb). Obrigatório para RDS.",
      customernameParam: "Prefixo do nome do cliente para arquivo ZIP (opcional)",
      tipCompress: "Use o switch -compress para criar automaticamente arquivos ZIP prontos para upload nesta ferramenta web.",
      requirementsPowershell: "PowerShell 5.1+, acesso ao SQL Server e credenciais de autenticação apropriadas.",
      usageInstructions: "Execute o script PowerShell no seu SQL Server com privilégios de administrador. O script gerará um arquivo ZIP contendo métricas de desempenho que você pode fazer upload acima para análise.",
      analyzing: "Analisando arquivos...",
      uploadSuccess: "Arquivos enviados com sucesso",
      uploadError: "Erro ao enviar arquivos",
      noFiles: "Nenhum arquivo selecionado",
      monthlyComparison: "Comparação de Custos Mensais",
      monthlyCost: "Custo Mensal ($)",
      performanceOverview: "Visão Geral do Desempenho",
      contact: "Contato",
      feedback: "Feedback",
      feedbackForm: "Enviar Formulário de Feedback",
      linkedinProfile: "Perfil do LinkedIn",
      footerNote: "SSATWeb - Ferramenta de Avaliação do SQL Server para Planejamento de Migração AWS RDS"
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    lng: 'en',
    debug: false,
    
    interpolation: {
      escapeValue: false,
    },
    
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

export default i18n;
